/**
 * Product page behaviour (sections/product.liquid):
 *
 * <product-gallery>   scroll-snap track with arrows, thumbnails and a "1 / 8" counter
 * <quantity-input>    minus and plus buttons around the quantity field
 * <variant-picker>    option radios: updates the variant id, prices, stock, buttons and URL
 * <sticky-buy-bar>    appears once the main add-to-cart button has scrolled out of view
 *
 * Adding to cart itself is handled by assets/cart.js (form[data-ajax-cart]).
 */
(function () {
  const strings = (() => {
    try {
      return JSON.parse(document.querySelector('[data-product-strings]')?.textContent || '{}');
    } catch {
      return {};
    }
  })();

  if (!customElements.get('product-gallery')) {
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-gallery-track]');
        this.slides = Array.from(this.track.children);
        this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
        this.prev = this.querySelector('[data-gallery-prev]');
        this.next = this.querySelector('[data-gallery-next]');
        this.current = this.querySelector('[data-gallery-current]');
        this.index = 0;
        if (this.slides.length < 2) return;

        this.prev?.addEventListener('click', () => this.go(this.index - 1));
        this.next?.addEventListener('click', () => this.go(this.index + 1));
        this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.go(Number(thumb.dataset.galleryThumb))));
        this.track.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') this.go(this.index + 1);
          if (event.key === 'ArrowLeft') this.go(this.index - 1);
        });

        // The slide that is mostly in view is the current one, whether reached by swipe, arrow or thumb.
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.update(this.slides.indexOf(entry.target));
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach((slide) => observer.observe(slide));
      }

      go(index) {
        const target = Math.max(0, Math.min(this.slides.length - 1, index));
        this.track.scrollTo({ left: this.slides[target].offsetLeft, behavior: 'smooth' });
        this.update(target);
      }

      /** Show the slide for a media id (variant images). */
      goToMedia(mediaId) {
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index > -1) this.go(index);
      }

      update(index) {
        if (index < 0 || index === this.index) return;
        this.index = index;
        this.slides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== index)));
        this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
        this.thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        if (this.prev) this.prev.disabled = index === 0;
        if (this.next) this.next.disabled = index === this.slides.length - 1;
        if (this.current) this.current.textContent = index + 1;
      }
    }
    customElements.define('product-gallery', ProductGallery);
  }

  if (!customElements.get('quantity-input')) {
    class QuantityInput extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.querySelectorAll('[data-quantity-step]').forEach((button) => {
          button.addEventListener('click', () => {
            const min = Number(this.input.min) || 1;
            const max = Number(this.input.max) || 99;
            const value = (Number(this.input.value) || min) + Number(button.dataset.quantityStep);
            this.input.value = Math.max(min, Math.min(max, value));
          });
        });
      }
    }
    customElements.define('quantity-input', QuantityInput);
  }

  if (!customElements.get('variant-picker')) {
    class VariantPicker extends HTMLElement {
      connectedCallback() {
        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
        this.form = document.getElementById(this.dataset.form);
        this.page = this.closest('[data-product-page]') || document;
        this.addEventListener('change', () => this.onChange());
      }

      onChange() {
        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        const selected = fieldsets.map((fieldset) => {
          const checked = fieldset.querySelector('input:checked');
          const label = fieldset.querySelector('[data-option-value]');
          if (label && checked) label.textContent = checked.value;
          return checked?.value;
        });
        const variant = this.variants.find((v) => v.options.every((option, i) => option === selected[i]));
        this.render(variant);
      }

      render(variant) {
        const buttons = document.querySelectorAll('[data-add-button]');
        const labels = document.querySelectorAll('[data-add-label]');

        if (!variant) {
          buttons.forEach((button) => (button.disabled = true));
          labels.forEach((label) => (label.textContent = strings.unavailable || ''));
          return;
        }

        const idInput = this.form?.querySelector('[data-variant-id]');
        if (idInput) idInput.value = variant.id;

        document.querySelectorAll('[data-price]').forEach((price) => (price.textContent = variant.price));
        const compare = this.page.querySelector('[data-compare]');
        if (compare) compare.hidden = !variant.compare;
        this.page.querySelector('[data-save]')?.toggleAttribute('hidden', !variant.compare);
        this.page.querySelector('[data-sale-badge]')?.toggleAttribute('hidden', !variant.compare);

        buttons.forEach((button) => (button.disabled = !variant.available));
        labels.forEach((label) => (label.textContent = variant.available ? strings.addToCart : strings.soldOut));
        this.page.querySelector('[data-stock]')?.classList.toggle('stock-status--sold-out', !variant.available);

        if (variant.media) this.page.querySelector('product-gallery')?.goToMedia(variant.media);

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }
    customElements.define('variant-picker', VariantPicker);
  }

  if (!customElements.get('sticky-buy-bar')) {
    class StickyBuyBar extends HTMLElement {
      connectedCallback() {
        const form = document.getElementById(this.dataset.form);
        const target = form?.querySelector('[data-add-button]');
        if (!target || !('IntersectionObserver' in window)) return;

        // Visible only while the main button is above the viewport, so it never covers the button.
        new IntersectionObserver(([entry]) => {
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', passed);
        }).observe(target);
      }
    }
    customElements.define('sticky-buy-bar', StickyBuyBar);
  }
})();
